// locator.js — store locator: Google Maps via assets/gmap.js, branded pins,
// filter chips (list + map pins in sync), "Use my location" distance sort and
// the postal-code match. Same guard + rescan() pattern as drawer.js/facets.js:
// `window.__tmgLocator` singleton, re-bound on `shopify:section:load`.
//
// The list, filters, geolocate and postal match must work even if the map
// never loads. When gmap.js is missing or no store has numeric lat/lng,
// initMap() adds `.map-unavailable` to the map panel and returns; every other
// binding is wired independently of it.
//
// POSTAL MATCH. Which store a postal code belongs to is decided by
// TMG_STORE_MATCH (store-match.js, loaded by the header on every page):
// the 250 km FSA rule, the same answer the header's pickup flyout, the
// homepage band and the inquiry forms give. Here
//   - inside a store area: the list is sorted by distance from the postal
//     area's point with each card's km printed (sortByPosition), the
//     matched store goes first marked "Your store", and the status line
//     names it;
//   - outside every store area: the list goes back to the merchant's order,
//     no store is marked, and the line says head office looks after that
//     area;
//   - not a postal code: the line asks for one.
// Cards find their store by name (data-store-name -> TMG_STORE_MATCH.keyOf).
// The field runs on Enter and on blur. A `?postal=` in the URL (what the
// header and homepage finders send when scripting is off) runs on arrival;
// failing that, the visitor's saved match does; and a match made in the
// header flyout on this page arrives as a `tmg:store` event.
if (!window.__tmgLocator) {
  (function () {
    var R_KM = 6371; // earth radius, km — haversine

    var mapPanel, payloadEl, listEl, postalInput, geoBtn, msgEl;
    var stores = [];
    var cardOrder = []; // the cards in the merchant's order, to go back to
    var lastPostal = ''; // the field value last run, so a blur after Enter is a no-op
    var map = null;
    var markerLayers = null; // { open: [pins], coming: [pins] }
    var userMarker = null;
    var userPos = null;
    var infoWin = null;
    var positions = {}; // store name -> Google's point for its address
    var currentFilter = 'all';
    var labels = { open: '', coming: '', storePage: '' };

    function reducedMotion() {
      return matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    function designMode() {
      return !!(window.Shopify && window.Shopify.designMode);
    }

    // ---------- haversine ----------
    function toRad(deg) {
      return (deg * Math.PI) / 180;
    }

    function haversineKm(lat1, lng1, lat2, lng2) {
      var dLat = toRad(lat2 - lat1);
      var dLng = toRad(lng2 - lng1);
      var a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
      return R_KM * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    }

    function hasCoords(s) {
      return !!s && typeof s.lat === 'number' && typeof s.lng === 'number' && isFinite(s.lat) && isFinite(s.lng);
    }

    function parsePayload(el) {
      if (!el) return [];
      try {
        var data = JSON.parse(el.textContent || '[]');
        return Array.isArray(data) ? data : [];
      } catch (e) {
        return [];
      }
    }

    // ---------- status line (geolocate / postal) ----------
    function showText(text) {
      if (!msgEl) return;
      msgEl.textContent = text || '';
      msgEl.hidden = !text;
    }

    function showMsg(kind) {
      if (!msgEl) return;
      showText(msgEl.getAttribute(kind === 'postal' ? 'data-postal-unknown' : 'data-geolocate-error'));
    }

    function hideMsg() {
      showText('');
    }

    // ---------- filter chips: list visibility + map layer visibility ----------
    // Named + stable listeners (hoisted to module scope, passed by reference to
    // addEventListener on every bind/rescan, preventing duplicate listener stacks).
    function onChipClick(e) {
      setFilter(e.currentTarget.getAttribute('data-locator-filter'));
    }

    function bindFilters() {
      var chips = document.querySelectorAll('[data-locator-filter]');
      Array.prototype.forEach.call(chips, function (chip) {
        chip.addEventListener('click', onChipClick);
      });
    }

    function setFilter(value) {
      currentFilter = value || 'all';

      var chips = document.querySelectorAll('[data-locator-filter]');
      Array.prototype.forEach.call(chips, function (chip) {
        var on = chip.getAttribute('data-locator-filter') === currentFilter;
        chip.classList.toggle('on', on);
        chip.setAttribute('aria-pressed', on ? 'true' : 'false');
      });

      var cardEls = listEl ? listEl.querySelectorAll('[data-locator-card]') : [];
      Array.prototype.forEach.call(cardEls, function (card) {
        var status = card.getAttribute('data-status');
        card.hidden = !(currentFilter === 'all' || status === currentFilter);
      });

      if (map && markerLayers) {
        ['open', 'coming'].forEach(function (status) {
          var show = currentFilter === 'all' || currentFilter === status;
          (markerLayers[status] || []).forEach(function (pin) {
            if (show && !pin.getMap()) pin.setMap(map);
            if (!show && pin.getMap()) pin.setMap(null);
          });
        });
      }
    }

    // ---------- map: Google Maps through gmap.js. With a key: the dark map,
    // the site's own pins, each moved onto Google's geocode of the store's
    // address. Without one: Google's plain embedded map of the first store,
    // and the list does the rest. ----------
    function storeCoords() {
      return stores.filter(hasCoords).map(function (s) {
        return positions[s.name] || new google.maps.LatLng(s.lat, s.lng);
      });
    }

    function fitBoundsPadded(coordsArr) {
      if (!map || !coordsArr.length) return;
      window.TMG_GMAP.fit(map, coordsArr, 12);
    }

    function refit() {
      var coordsArr = storeCoords();
      if (userPos) coordsArr.push(userPos);
      fitBoundsPadded(coordsArr);
    }

    // Popup content is built with DOM methods (createElement/textContent),
    // never innerHTML — store name/url come from the JSON payload, which is
    // merchant-entered section-block data, not hardcoded-safe markup.
    function buildPopup(s) {
      var wrap = document.createElement('div');
      wrap.className = 'map-popup';

      var nameEl = document.createElement('strong');
      nameEl.className = 'mp-name';
      nameEl.textContent = s.name || '';
      wrap.appendChild(nameEl);

      var chip = document.createElement('span');
      chip.className = 'mp-chip' + (s.status === 'open' ? ' open' : '');
      chip.textContent = s.status === 'open' ? labels.open : labels.coming;
      wrap.appendChild(chip);

      if (s.url) {
        var link = document.createElement('a');
        link.className = 'mp-link';
        link.href = s.url;
        link.textContent = labels.storePage;
        wrap.appendChild(link);
      }

      return wrap;
    }

    function initMap() {
      if (!mapPanel) return;
      map = null;
      markerLayers = null;
      userMarker = null;
      userPos = null;
      mapPanel.classList.remove('map-unavailable');

      var validStores = stores.filter(hasCoords);
      if (!window.TMG_GMAP || validStores.length === 0) {
        mapPanel.classList.add('map-unavailable');
        return;
      }

      labels.open = mapPanel.getAttribute('data-open-label') || '';
      labels.coming = mapPanel.getAttribute('data-coming-label') || '';
      labels.storePage = mapPanel.getAttribute('data-store-page-label') || '';

      var panel = mapPanel;
      window.TMG_GMAP.ready(function (ok) {
        if (panel !== mapPanel) return; // a rescan replaced the section meanwhile
        if (!ok) {
          var first = validStores.filter(function (s) { return s.status === 'open'; })[0] || validStores[0];
          if (!panel.querySelector('.gmap-embed')) window.TMG_GMAP.embed(panel, first.address || first.lat + ',' + first.lng, 11, first.name);
          return;
        }
        panel.innerHTML = '';
        map = window.TMG_GMAP.create(panel, {});
        panel.classList.add('is-map');
        var firstOpen = validStores.filter(function (s) { return s.status === 'open'; })[0] || validStores[0];
        window.TMG_GMAP.fallback(panel, firstOpen.address || firstOpen.lat + ',' + firstOpen.lng, 11, firstOpen.name, map);
        infoWin = new google.maps.InfoWindow();
        markerLayers = { open: [], coming: [] };

        validStores.forEach(function (s) {
          var stored = new google.maps.LatLng(s.lat, s.lng);
          var status = s.status === 'open' ? 'open' : 'coming';
          var pin = window.TMG_GMAP.pin(map, stored, status === 'open' ? 'pin-open' : 'pin-coming', function () {
            infoWin.setContent(buildPopup(s));
            infoWin.setPosition(pin.position);
            infoWin.open({ map: map });
          });
          markerLayers[status].push(pin);
          window.TMG_GMAP.locate(s.address, stored, function (pos) {
            positions[s.name] = pos;
            pin.moveTo(pos);
            refit();
          });
        });

        refit();
        setFilter(currentFilter);
      });
    }

    function placeUserMarker(lat, lng) {
      if (!map) return;
      if (userMarker) userMarker.setMap(null);
      userPos = new google.maps.LatLng(lat, lng);
      userMarker = window.TMG_GMAP.pin(map, userPos, 'pin-user');
      refit();
    }

    function clearUserMarker() {
      if (!map || !userMarker) return;
      userMarker.setMap(null);
      userMarker = null;
      userPos = null;
      refit();
    }

    // ---------- distance sort — shared by geolocate + the postal match, and
    // exposed on window.__tmgLocator so it's independently callable with a
    // mocked position (list must sort correctly even with no map). ----------
    function sortByPosition(lat, lng) {
      if (typeof lat !== 'number' || typeof lng !== 'number' || !isFinite(lat) || !isFinite(lng)) return;
      if (!listEl) return;

      var cardEls = Array.prototype.slice.call(listEl.querySelectorAll('[data-locator-card]'));
      var withDist = cardEls.map(function (card) {
        var cLat = parseFloat(card.getAttribute('data-lat'));
        var cLng = parseFloat(card.getAttribute('data-lng'));
        var dist = isFinite(cLat) && isFinite(cLng) ? haversineKm(lat, lng, cLat, cLng) : null;
        return { card: card, dist: dist };
      });

      withDist.forEach(function (item) {
        var distEl = item.card.querySelector('[data-locator-dist]');
        if (!distEl) return;
        if (item.dist === null) {
          distEl.hidden = true;
          distEl.textContent = '';
        } else {
          distEl.hidden = false;
          distEl.textContent = Math.round(item.dist) + ' km';
        }
      });

      // Ascending by distance; cards with no known coordinates sink to the
      // end (stable relative order among themselves — Array#sort is stable
      // in every engine this theme targets).
      withDist.sort(function (a, b) {
        if (a.dist === null && b.dist === null) return 0;
        if (a.dist === null) return 1;
        if (b.dist === null) return -1;
        return a.dist - b.dist;
      });

      withDist.forEach(function (item) {
        listEl.appendChild(item.card);
      });

      placeUserMarker(lat, lng);
    }

    // ---------- postal match ----------
    // Marks the card for store `key` (a TMG_STORE_MATCH key; '' clears the
    // mark) and moves it to the top of the list. Returns that card or null.
    function markStore(key) {
      var M = window.TMG_STORE_MATCH;
      var hit = null;
      if (!listEl) return null;
      Array.prototype.forEach.call(listEl.querySelectorAll('[data-locator-card]'), function (card) {
        var on = !!(key && M && M.keyOf(card.getAttribute('data-store-name')) === key);
        card.classList.toggle('is-match', on);
        var tag = card.querySelector('[data-locator-you]');
        if (tag) tag.hidden = !on;
        if (on && !hit) hit = card;
      });
      if (hit) listEl.insertBefore(hit, listEl.firstChild);
      return hit;
    }

    // Back to the merchant's order, no distances, no mark, no postal pin.
    function resetList() {
      if (!listEl) return;
      cardOrder.forEach(function (card) {
        listEl.appendChild(card);
        var distEl = card.querySelector('[data-locator-dist]');
        if (distEl) {
          distEl.hidden = true;
          distEl.textContent = '';
        }
      });
      markStore('');
      clearUserMarker();
    }

    // r: a TMG_STORE_MATCH.match() result, or null for "not a postal code".
    function applyMatch(r) {
      var M = window.TMG_STORE_MATCH;
      if (!M) return;
      if (!r) {
        resetList();
        showMsg('postal');
      } else if (r.store) {
        sortByPosition(r.lat, r.lng);
        var card = markStore(r.store);
        var template = msgEl ? msgEl.getAttribute('data-postal-match') : '';
        showText(M.fill(template, r, card ? card.getAttribute('data-store-name') : r.name));
      } else {
        resetList();
        showText(M.fill(msgEl ? msgEl.getAttribute('data-postal-outside') : '', r));
      }
    }

    function handlePostal() {
      if (!postalInput) return;
      var raw = (postalInput.value || '').trim();
      if (!raw || raw === lastPostal) return;
      lastPostal = raw;
      var M = window.TMG_STORE_MATCH;
      if (!M) {
        showMsg('postal');
        return;
      }
      var r = M.match(raw);
      if (r) M.save(r); // fires tmg:store -> onStore -> applyMatch, and repaints the header pill
      else applyMatch(null);
    }

    function onStore(e) {
      var r = e.detail;
      if (!listEl || !r) return;
      if (postalInput && document.activeElement !== postalInput) postalInput.value = r.code;
      if (postalInput) lastPostal = (postalInput.value || '').trim();
      applyMatch(r);
    }

    function onPostalKeydown(e) {
      if (e.key === 'Enter') {
        e.preventDefault();
        handlePostal();
      }
    }

    function bindPostal() {
      if (!postalInput) return;
      postalInput.addEventListener('keydown', onPostalKeydown);
      postalInput.addEventListener('blur', handlePostal);
    }

    // ?postal= first (the no-script GET from the header and homepage
    // finders), then the visitor's saved match. The theme editor skips the
    // saved match so the merchant sees the list in their own order.
    function applyArrival() {
      var M = window.TMG_STORE_MATCH;
      if (!postalInput || !M) return;
      var q = '';
      try {
        q = new URLSearchParams(window.location.search).get('postal') || '';
      } catch (e) {
        q = '';
      }
      if (q.trim()) {
        postalInput.value = q;
        lastPostal = '';
        handlePostal();
        return;
      }
      if (designMode()) return;
      var r = M.saved();
      if (r) {
        postalInput.value = r.code;
        lastPostal = r.code;
        applyMatch(r);
      }
    }

    // ---------- geolocate ----------
    function onGeolocateClick() {
      hideMsg();
      if (!('geolocation' in navigator)) {
        showMsg('geolocate');
        return;
      }
      navigator.geolocation.getCurrentPosition(
        function (pos) {
          hideMsg();
          markStore('');
          lastPostal = '';
          sortByPosition(pos.coords.latitude, pos.coords.longitude);
        },
        function () {
          showMsg('geolocate');
        },
        { enableHighAccuracy: false, timeout: 8000, maximumAge: 300000 }
      );
    }

    function bindGeolocate() {
      if (!geoBtn) return;
      geoBtn.addEventListener('click', onGeolocateClick);
    }

    // ---------- bind / rescan ----------
    function bind() {
      mapPanel = document.querySelector('[data-locator-map]');
      payloadEl = document.querySelector('script[data-locator-data]');
      listEl = document.querySelector('[data-locator-list]');
      postalInput = document.querySelector('[data-locator-postal]');
      geoBtn = document.querySelector('[data-locator-geolocate]');
      msgEl = document.querySelector('[data-locator-msg]');

      if (!listEl) return; // no locator section on this page

      stores = parsePayload(payloadEl);
      cardOrder = Array.prototype.slice.call(listEl.querySelectorAll('[data-locator-card]'));
      currentFilter = 'all';
      lastPostal = '';
      hideMsg();

      bindFilters();
      bindGeolocate();
      bindPostal();
      initMap();
      setFilter('all');
      applyArrival();
    }

    function rescan() {
      bind();
    }

    document.addEventListener('tmg:store', onStore);
    document.addEventListener('shopify:section:load', function () {
      window.__tmgLocator.rescan();
    });

    bind();
    window.__tmgLocator = { rescan: rescan, sortByPosition: sortByPosition };
  })();
}
